import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Card, Badge, Modal, SlidePanel, toast } from '@/components';
import { PanelSection, SpecGrid, SpecCell } from '@/components/SlidePanel';
import { PdfGenerateModal } from '@/components/PdfGenerateModal';
import { ExcelDocumentModal } from '@/components/ExcelDocumentModal';
import { TemplateUpdateBanner } from '@/components/TemplateUpdateBanner';
import { DealManagement } from '@/components/DealManagement';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { buildApplyUrl } from '@/lib/apply-url';
import styles from './Contracts.module.css';

interface Contract {
  id: number | string; contract_type: string; title: string; customer_name: string;
  status: string; created_at: string; assignee?: string; amount?: number;
  customer_id?: string; property_id?: string;
  important_explanation_delivered_at?: string; important_explanation_confirmed_at?: string;
  renewal_type?: string; contract_start?: string; contract_end?: string;
  signed_pdf_url?: string | null; signed_pdf_uploaded_at?: string | null; retention_until?: string | null;
}
interface Customer { id: string; name: string; }
interface Property { id: string; name: string; address: string; rent: number; layout?: string; area?: number; owner_name?: string; }
interface Submission { id: string; form_id: string; data: Record<string, string>; status: string; property_name?: string; property_address?: string; created_at: string; excel_doc_id?: string; has_document?: boolean; has_pdf?: boolean; }

const subStatusLabel: Record<string, string> = { received: '\u53D7\u4ED8', reviewing: '\u5BE9\u67FB\u4E2D', approved: '\u627F\u8A8D', rejected: '\u5374\u4E0B' };
const subStatusVariant: Record<string, 'gray' | 'blue' | 'green' | 'red' | 'amber'> = { received: 'blue', reviewing: 'amber', approved: 'green', rejected: 'red' };

const typeLabel: Record<string, string> = { lease: '賃貸借契約書', important_explanation: '重要事項説明書', sales: '売買契約書', management: '管理委託契約書', application: '入居申込書', owner_report: 'オーナー収支報告書' };
const typeIcon: Record<string, string> = { lease: '📄', important_explanation: '⚖️', sales: '🤝', management: '🔑', application: '✏️', owner_report: '💰' };
const statusLabel: Record<string, string> = { draft: '作成中', pending: '確認中', sent: '送付済', signed: '署名済', expired: '期限切れ' };
const statusVariant: Record<string, 'gray' | 'blue' | 'green' | 'red' | 'amber'> = { draft: 'gray', pending: 'amber', sent: 'blue', signed: 'green', expired: 'red' };
const statusActionLabel: Record<string, string> = { draft: '作成中', pending: '宅建士確認待ち', sent: '送付済', signed: '電子署名完了', expired: '期限切れ' };
const fmtDate = (d: string) => { const dt = new Date(d); return `${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getDate()).padStart(2,'0')}`; };

const docCards = [
  { icon: '📄', type: 'lease', title: '賃貸借契約書', desc: 'AI自動入力・電子署名リンク送付。顧客・物件データから自動生成。', badge: '電子署名対応', badgeClass: '' },
  { icon: '⚖️', type: 'important_explanation', title: '重要事項説明書', desc: '物件情報・法的事項を自動反映。宅建士確認フロー付き。', badge: '宅建士確認要', badgeClass: 'check' },
  { icon: '🤝', type: 'sales', title: '売買契約書', desc: '売買価格・引渡し日・特約事項を自動挿入。', badge: '電子署名対応', badgeClass: '' },
  { icon: '🔑', type: 'management', title: '管理委託契約書', desc: 'オーナー情報・管理料率を自動入力。', badge: '電子署名対応', badgeClass: '' },
  { icon: '✏️', type: 'application', title: '入居申込書', desc: '顧客が直接入力できるWebフォーム。審査会社への一括申請。', badge: 'Webフォーム', badgeClass: 'web' },
  { icon: '💰', type: 'owner_report', title: 'オーナー収支報告書', desc: '入金データから月次収支を自動集計。PDF送付。', badge: 'PDF自動生成', badgeClass: 'pdf' },
  { icon: '📊', type: 'excel_template', title: 'Excel帳票', desc: '重説・助成金申請書等のExcel帳票。DBデータ自動注入→PDF出力。', badge: 'Excel→PDF', badgeClass: 'pdf' },
];

const aiFields: Record<string, { auto: string[]; manual: string[] }> = {
  lease: { auto: ['賃料', '敷金・礼金', '契約期間', '物件所在地', '借主氏名・住所', '貸主氏名・住所', '管理会社情報', '更新料'], manual: ['特約事項'] },
  important_explanation: { auto: ['物件表示', '建築年月・構造', '用途地域・建蔽率', '設備（給排水・電気・ガス）', '管理形態・管理会社', '敷金・礼金'], manual: ['アスベスト・耐震'] },
  sales: { auto: ['売主・買主情報', '物件表示・登記情報', '売買価格・手付金', '残代金・引渡し日'], manual: ['ローン特約条件', '特約事項'] },
  management: { auto: ['オーナー情報', '管理手数料率', '管理物件一覧', '送金口座'], manual: ['特約事項'] },
  application: { auto: ['顧客情報', '希望物件', '勤務先情報'], manual: ['連帯保証人'] },
  owner_report: { auto: ['家賃収入', '管理手数料', '修繕費', '送金額'], manual: [] },
};

const signFlowDefs: Record<string, { steps: string[]; descs: string[] }> = {
  lease: { steps: ['書類生成', '貸主（オーナー）署名', '借主（入居者）署名', '宅建士確認・完了'], descs: ['顧客・物件データから自動生成。特約事項を確認・編集。', '署名リンクをSMSまたはメールで送付。', '署名リンクをSMSまたはメールで送付。', '完了後、両者に署名済PDFを自動送付。'] },
  important_explanation: { steps: ['書類生成', '内容確認・修正', '宅建士レビュー・記名押印', '借主への交付・説明'], descs: ['物件情報・法的制限・設備状況をDBから自動取得して生成。', '担当者が法的事項・特記事項を確認・修正。', '宅建士が内容を確認し電子署名。', '対面 or オンラインで説明実施後、借主に署名してもらいます。'] },
  sales: { steps: ['書類生成', '売主署名', '買主署名', '完了'], descs: ['売買条件を自動入力。', '署名リンク送付。', '署名リンク送付。', '両者に署名済PDFを自動送付。'] },
  management: { steps: ['書類生成', 'オーナー署名', '管理会社署名', '完了'], descs: ['管理条件を自動入力。', '署名リンク送付。', '管理会社が確認・署名。', '両者に署名済PDFを自動送付。'] },
};

export function Contracts() {
  // 検索・ページング state
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const contractsParams = new URLSearchParams({
    paginated: 'true',
    limit: String(pageSize),
    offset: String((page - 1) * pageSize),
    ...(search ? { q: search } : {}),
  });
  const { data: contractsData, loading, refetch } = useApi<{ items: Contract[]; total: number }>(
    `/tenant/contracts?${contractsParams.toString()}`
  );
  const contracts = contractsData?.items ?? [];
  const contractsTotal = contractsData?.total ?? 0;
  const contractsTotalPages = Math.max(1, Math.ceil(contractsTotal / pageSize));

  const { data: customers } = useApi<Customer[]>('/tenant/customers');
  const { data: properties } = useApi<Property[]>('/tenant/properties');
  // 書類種別カードの件数集計用(検索・ページング非対応、全件取得)
  const { data: allContractsForStats } = useApi<Contract[]>('/tenant/contracts');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selected, setSelected] = useState<Contract | null>(null);
  const [typePanel, setTypePanel] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Contract | null>(null);
  const [createType, setCreateType] = useState('lease');
  const [createFromCard, setCreateFromCard] = useState(false);
  const [form, setForm] = useState({ title: '', customer_id: '', customer_name: '', status: 'draft', property_id: '', amount: '', contract_start: '', contract_end: '', renewal_type: 'agreement' });
  const [editForm, setEditForm] = useState({ title: '', status: '', customer_name: '', amount: '', renewal_type: 'agreement', contract_start: '', contract_end: '' });
  const [ownerCheckAll, setOwnerCheckAll] = useState(true);
  const [ownerChecked, setOwnerChecked] = useState<Set<string>>(new Set());
  // 紙運用: 署名済 PDF アップロード用 input ref
  const signedPdfInputRef = useRef<HTMLInputElement>(null);
  const [uploadingSignedPdf, setUploadingSignedPdf] = useState(false);

  const handleSignedPdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 同じファイル再選択を許可
    if (!file || !selected) return;
    if (file.type !== 'application/pdf') { toast('PDF ファイルを選択してください', 'error'); return; }
    if (!confirm(`「${selected.title}」に署名済 PDF をアップロードします。ステータスが「署名済」に変わります。`)) return;
    setUploadingSignedPdf(true);
    try {
      const { token, user } = useAuthStore.getState();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (user?.tenantSlug) headers['X-Tenant-Slug'] = user.tenantSlug;
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/tenant/contracts/${selected.id}/upload-signed-pdf`, { method: 'POST', headers, body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || `HTTP ${res.status}`);
      }
      const updated = await res.json() as Contract;
      toast('署名済 PDF をアップロードしました', 'success');
      setSelected(updated);
      refetch();
    } catch (err) {
      toast((err as Error)?.message || 'アップロードに失敗しました', 'error');
    } finally {
      setUploadingSignedPdf(false);
    }
  };

  // Excel帳票モーダル
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelDealId, setExcelDealId] = useState<string | undefined>();
  const [excelPropertyId, setExcelPropertyId] = useState<string | undefined>();
  const [excelCustomerId, setExcelCustomerId] = useState<string | undefined>();
  const [excelPropertyName, setExcelPropertyName] = useState<string | undefined>();
  const [excelCustomerName, setExcelCustomerName] = useState<string | undefined>();
  const [excelCategory, setExcelCategory] = useState<string | undefined>();
  const [excelContractId, setExcelContractId] = useState<string | undefined>();
  const [excelSubmissionId, setExcelSubmissionId] = useState<string | undefined>();
  const [excelExistingDocId, setExcelExistingDocId] = useState<string | undefined>();

  // 書類詳細で対応するExcel帳票のID（PDF表示用）
  const [selectedExcelDocId, setSelectedExcelDocId] = useState<string | null>(null);
  useEffect(() => {
    if (!selected) { setSelectedExcelDocId(null); return; }
    (async () => {
      try {
        // contract_id で検索
        let docs = await api<{ id: string; contract_id: string; category: string; submission_id: string; pdf_generated_at: string | null }[]>(`/tenant/excel-documents?contract_id=${selected.id}`);
        // contract_idで見つからない場合: カテゴリ or submission で全件から検索
        if (!docs.length) {
          const allDocs = await api<{ id: string; contract_id: string; category: string; submission_id: string; pdf_generated_at: string | null }[]>('/tenant/excel-documents');
          const categoryMap: Record<string, string> = { important_explanation: 'important_explanation', lease: 'lease', sale: 'sales', management: 'management', application: 'other' };
          const cat = categoryMap[selected.contract_type];
          // 入居申込書: submission_id があるもの、またはカテゴリ一致でcontract_id未設定のもの
          if (cat) docs = allDocs.filter(d => (d.category === cat && !d.contract_id) || (selected.contract_type === 'application' && d.submission_id));
        }
        const withPdf = docs.find(d => d.pdf_generated_at);
        setSelectedExcelDocId(withPdf?.id || docs[0]?.id || null);
      } catch { setSelectedExcelDocId(null); }
    })();
  }, [selected?.id]);

  // 入居申込フォーム
  const [showApplyCreate, setShowApplyCreate] = useState(false);
  const [applyPropertyId, setApplyPropertyId] = useState('');
  const [applyForms, setApplyForms] = useState<{ id: string; property_name: string; token: string; status: string; created_at: string }[]>([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [selectedSub, setSelectedSub] = useState<Submission | null>(null);

  // PDF生成モーダル
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfModalType, setPdfModalType] = useState('lease');
  const [pdfPropertyId, setPdfPropertyId] = useState<string | undefined>();
  const [pdfCustomerId, setPdfCustomerId] = useState<string | undefined>();
  const [pdfContractId, setPdfContractId] = useState<string | undefined>();

  // オーナー収支報告書
  interface OwnerReport { id: string; owner_name: string; target_month: string; rent_income: number; management_fee: number; repair_cost: number; transfer_amount: number; properties: { name: string; address: string; rent: number }[]; status: string; created_at: string }
  const [ownerReports, setOwnerReports] = useState<OwnerReport[]>([]);
  const [ownerMonth, setOwnerMonth] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`);
  const [generatingOwner, setGeneratingOwner] = useState(false);

  const { user } = useAuthStore.getState();
  const tenantSlug = user?.tenantSlug ?? 'tenant';

  const fetchApplyForms = async () => {
    try {
      const data = await api<typeof applyForms>('/tenant/application-forms');
      setApplyForms(data);
    } catch { /* ignore */ }
  };

  const handleCreateApplyForm = async () => {
    if (!applyPropertyId) { toast('\u7269\u4EF6\u3092\u9078\u629E\u3057\u3066\u304F\u3060\u3055\u3044', 'error'); return; }
    try {
      const form = await api<{ token: string }>('/tenant/application-forms', { method: 'POST', body: JSON.stringify({ property_id: applyPropertyId }) });
      const url = buildApplyUrl(form.token, { slug: tenantSlug });
      setShowApplyCreate(false);
      setShareUrl(url);
      setShowShareModal(true);
      fetchApplyForms();
      toast('\u30D5\u30A9\u30FC\u30E0\u3092\u4F5C\u6210\u3057\u307E\u3057\u305F', 'success');
    } catch { toast('\u4F5C\u6210\u306B\u5931\u6557\u3057\u307E\u3057\u305F', 'error'); }
  };

  const fetchSubmissions = async () => {
    try {
      const data = await api<Submission[]>('/tenant/application-submissions');
      setSubmissions(data);
    } catch { /* ignore */ }
  };

  const handleApplicationPdf = (contract: Contract) => {
    const sub = submissions.find(s => s.data?.name);
    const d = sub?.data ?? {};
    const today = new Date().toLocaleDateString('ja-JP');
    const rows = [
      ['\u6C0F\u540D', d.name], ['\u30D5\u30EA\u30AC\u30CA', d.name_kana], ['\u751F\u5E74\u6708\u65E5', d.birth_date],
      ['\u96FB\u8A71\u756A\u53F7', d.phone], ['\u30E1\u30FC\u30EB', d.email], ['\u73FE\u4F4F\u6240', d.current_address],
      ['\u52E4\u52D9\u5148', d.company_name], ['\u52E4\u52D9\u5148\u4F4F\u6240', d.company_address], ['\u52E4\u52D9\u5148\u96FB\u8A71', d.company_phone],
      ['\u5E74\u53CE\uFF08\u4E07\u5186\uFF09', d.annual_income], ['\u52E4\u7D9A\u5E74\u6570', d.years_employed],
      ['\u4FDD\u8A3C\u4EBA\u6C0F\u540D', d.guarantor_name], ['\u4FDD\u8A3C\u4EBA\u4F4F\u6240', d.guarantor_address],
      ['\u4FDD\u8A3C\u4EBA\u96FB\u8A71', d.guarantor_phone], ['\u4FDD\u8A3C\u4EBA\u52E4\u52D9\u5148', d.guarantor_company],
      ['\u5165\u5C45\u5E0C\u671B\u65E5', d.move_in_date], ['\u5165\u5C45\u4EBA\u6570', d.num_residents], ['\u5099\u8003', d.notes],
    ].filter(([, v]) => v).map(([k, v]) => `<tr><th style="padding:8px 12px;border:1px solid #ddd;background:#f5f5f5;text-align:left;width:140px;font-size:12px">${k}</th><td style="padding:8px 12px;border:1px solid #ddd;font-size:13px">${v}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html lang="ja"><head><meta charset="utf-8"><title>\u5165\u5C45\u7533\u8FBC\u66F8 - ${d.name || contract.customer_name}</title>
<style>@media print{body{margin:0}.no-print{display:none!important}}body{font-family:'Helvetica Neue',Arial,'Noto Sans JP',sans-serif;color:#1a1916;max-width:700px;margin:30px auto;padding:20px;line-height:1.6}table{width:100%;border-collapse:collapse;margin:16px 0}</style></head>
<body>
<h1 style="text-align:center;font-size:22px;font-weight:800;margin-bottom:4px">\u5165\u5C45\u7533\u8FBC\u66F8</h1>
<p style="text-align:center;font-size:12px;color:#666">${contract.customer_name}\u69D8 | ${today}</p>
<table>${rows}</table>
<div style="margin-top:24px;font-size:11px;color:#999;text-align:center">\u5370\u5237\u65E5: ${today}</div>
<button class="no-print" onclick="window.print()" style="display:block;margin:24px auto;padding:10px 30px;background:#dc2626;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer">\u5370\u5237\u3059\u308B</button>
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const handleApplicationResend = async (_contract: Contract) => {
    // 最新の申込フォームURLをクリップボードにコピーし、共有モーダルを開く
    if (applyForms.length > 0) {
      const url = buildApplyUrl(applyForms[0].token, { slug: tenantSlug });
      setShareUrl(url);
      setShowShareModal(true);
    } else {
      toast('\u5148\u306B\u7533\u8FBC\u30D5\u30A9\u30FC\u30E0\u3092\u4F5C\u6210\u3057\u3066\u304F\u3060\u3055\u3044', 'info');
    }
  };

  const handleSubStatus = async (subId: string, status: string) => {
    try {
      await api(`/tenant/application-submissions/${subId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      toast(status === 'approved' ? '\u627F\u8A8D\u3057\u307E\u3057\u305F\u3002\u901A\u77E5\u30E1\u30FC\u30EB\u3092\u9001\u4FE1\u3057\u307E\u3057\u305F' : status === 'rejected' ? '\u5374\u4E0B\u3057\u307E\u3057\u305F\u3002\u901A\u77E5\u30E1\u30FC\u30EB\u3092\u9001\u4FE1\u3057\u307E\u3057\u305F' : '\u30B9\u30C6\u30FC\u30BF\u30B9\u3092\u66F4\u65B0\u3057\u307E\u3057\u305F', 'success');
      fetchSubmissions();
      setSelectedSub(null);
    } catch { toast('\u66F4\u65B0\u306B\u5931\u6557\u3057\u307E\u3057\u305F', 'error'); }
  };

  const fetchOwnerReports = async (month?: string) => {
    try {
      const data = await api<OwnerReport[]>(`/tenant/owner-reports?month=${month || ownerMonth}`);
      setOwnerReports(data);
    } catch { /* ignore */ }
  };

  const handleGenerateOwnerReports = async () => {
    setGeneratingOwner(true);
    try {
      const res = await api<{ generated: number }>('/tenant/owner-reports/generate', { method: 'POST', body: JSON.stringify({ month: ownerMonth }) });
      toast(`${res.generated}\u4EF6\u306E\u30EC\u30DD\u30FC\u30C8\u3092\u751F\u6210\u3057\u307E\u3057\u305F`, 'success');
      fetchOwnerReports(ownerMonth);
    } catch { toast('\u751F\u6210\u306B\u5931\u6557\u3057\u307E\u3057\u305F', 'error'); }
    finally { setGeneratingOwner(false); }
  };

  const handleOwnerReportPdf = (report: OwnerReport) => {
    const today = new Date().toLocaleDateString('ja-JP');
    const propRows = report.properties.map((p, i) => `<tr><td style="padding:8px 12px;border:1px solid #ddd;text-align:center">${i + 1}</td><td style="padding:8px 12px;border:1px solid #ddd">${p.name}</td><td style="padding:8px 12px;border:1px solid #ddd">${p.address}</td><td style="padding:8px 12px;border:1px solid #ddd;text-align:right;font-family:monospace">\u00A5${p.rent.toLocaleString()}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html lang="ja"><head><meta charset="utf-8"><title>\u53CE\u652F\u5831\u544A\u66F8 - ${report.owner_name}</title>
<style>@media print{body{margin:0}.no-print{display:none!important}}body{font-family:'Helvetica Neue',Arial,'Noto Sans JP',sans-serif;color:#1a1916;max-width:700px;margin:30px auto;padding:20px;line-height:1.6}table{width:100%;border-collapse:collapse}th{padding:8px 12px;border:1px solid #ddd;background:#f5f5f5;font-size:12px;text-align:left}</style></head>
<body>
<h1 style="text-align:center;font-size:22px">\u53CE\u652F\u5831\u544A\u66F8</h1>
<p style="text-align:center;font-size:14px;color:#666">${report.target_month} \u5206</p>
<p style="font-size:16px"><strong>${report.owner_name}</strong> \u69D8</p>
<table><thead><tr><th style="width:30px">No.</th><th>\u7269\u4EF6\u540D</th><th>\u4F4F\u6240</th><th>\u5BB6\u8CC3\u53CE\u5165</th></tr></thead><tbody>${propRows}</tbody></table>
<div style="margin:24px 0;padding:16px;background:#f8f9fa;border-radius:8px;font-size:14px">
<div style="display:flex;justify-content:space-between;margin-bottom:8px"><span>\u5BB6\u8CC3\u53CE\u5165\u5408\u8A08</span><strong style="font-family:monospace">\u00A5${report.rent_income.toLocaleString()}</strong></div>
<div style="display:flex;justify-content:space-between;margin-bottom:8px"><span>\u7BA1\u7406\u624B\u6570\u6599(5%)</span><span style="font-family:monospace">-\u00A5${report.management_fee.toLocaleString()}</span></div>
<div style="display:flex;justify-content:space-between;margin-bottom:8px"><span>\u4FEE\u7E55\u8CBB</span><span style="font-family:monospace">-\u00A5${report.repair_cost.toLocaleString()}</span></div>
<hr style="border:none;border-top:2px solid #333;margin:8px 0">
<div style="display:flex;justify-content:space-between;font-size:18px"><strong>\u9001\u91D1\u984D</strong><strong style="font-family:monospace;color:#dc2626">\u00A5${report.transfer_amount.toLocaleString()}</strong></div>
</div>
<div style="text-align:center;font-size:11px;color:#999;margin-top:24px">\u5370\u5237\u65E5: ${today}</div>
<button class="no-print" onclick="window.print()" style="display:block;margin:24px auto;padding:10px 30px;background:#dc2626;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer">\u5370\u5237\u3059\u308B</button>
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const handleOwnerReportSend = async (reportId: string) => {
    try {
      await api(`/tenant/owner-reports/${reportId}/send`, { method: 'POST' });
      toast('\u9001\u4ED8\u3057\u307E\u3057\u305F', 'success');
      fetchOwnerReports(ownerMonth);
    } catch { toast('\u9001\u4ED8\u306B\u5931\u6557\u3057\u307E\u3057\u305F', 'error'); }
  };

  const handleOwnerBulkSend = async () => {
    try {
      const res = await api<{ sent: number }>('/tenant/owner-reports/bulk-send', { method: 'POST', body: JSON.stringify({ month: ownerMonth }) });
      toast(`${res.sent}\u4EF6\u9001\u4ED8\u3057\u307E\u3057\u305F`, 'success');
      fetchOwnerReports(ownerMonth);
    } catch { toast('\u4E00\u62EC\u9001\u4ED8\u306B\u5931\u6557\u3057\u307E\u3057\u305F', 'error'); }
  };

  useEffect(() => {
    if (typePanel === 'application') { fetchApplyForms(); fetchSubmissions(); }
    if (typePanel === 'owner_report') { fetchOwnerReports(); }
  }, [typePanel]);

  const handleOpenTemplate = (type: string, propertyId?: string, customerId?: string, contractId?: string) => {
    setPdfModalType(type);
    setPdfPropertyId(propertyId);
    setPdfCustomerId(customerId);
    setPdfContractId(contractId);
    setShowPdfModal(true);
  };

  const handleOpenCreate = (type?: string) => {
    setCreateType(type || 'lease');
    setCreateFromCard(!!type);
    setTypePanel(null);
    setForm({ title: '', customer_id: '', customer_name: '', status: 'draft', property_id: '', amount: '', contract_start: '', contract_end: '', renewal_type: 'agreement' });
    setShowCreate(true);
  };
  const handleCreate = async () => {
    if (!form.customer_name) { toast('顧客は必須です', 'error'); return; }
    const title = form.title || typeLabel[createType];
    try {
      await api('/tenant/contracts', { method: 'POST', body: JSON.stringify({ contract_type: createType, title, customer_id: form.customer_id || undefined, customer_name: form.customer_name, status: form.status, property_id: form.property_id || undefined, amount: Number(form.amount) || 0, renewal_type: form.renewal_type || 'agreement', contract_start: form.contract_start || undefined, contract_end: form.contract_end || undefined }) });
      toast('✓ 書類を生成しました。内容を確認してください', 'success'); setShowCreate(false); refetch();
    } catch (e) { toast(e instanceof Error ? e.message : '作成に失敗しました', 'error'); }
  };
  const handleCustomerSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value; const customer = (customers ?? []).find(c => c.id === id);
    setForm({ ...form, customer_id: id, customer_name: customer?.name || '' });
  };
  const handleOpenEdit = () => {
    if (!selected) return; setEditTarget(selected);
    setEditForm({ title: selected.title, status: selected.status, customer_name: selected.customer_name, amount: String(selected.amount || ''), renewal_type: selected.renewal_type || 'agreement', contract_start: selected.contract_start || '', contract_end: selected.contract_end || '' });
    setShowEdit(true);
  };
  const handleEdit = async () => {
    if (!editTarget) return;
    try { await api(`/tenant/contracts/${editTarget.id}`, { method: 'PATCH', body: JSON.stringify({ ...editForm, amount: Number(editForm.amount) || 0, renewal_type: editForm.renewal_type, contract_start: editForm.contract_start || null, contract_end: editForm.contract_end || null }) });
      toast('書類を更新しました', 'success'); setShowEdit(false); setEditTarget(null); setSelected(null); refetch();
    } catch (e) { toast(e instanceof Error ? e.message : '更新に失敗しました', 'error'); }
  };
  const handleConfirmExplanation = async () => {
    if (!selected) return;
    try { await api(`/tenant/contracts/${selected.id}/confirm-explanation`, { method: 'PATCH' }); toast('宅建士確認を記録しました', 'success'); refetch();
      const updated = await api<Contract>(`/tenant/contracts/${selected.id}`, { method: 'GET' }); setSelected(updated);
    } catch (e) { toast(e instanceof Error ? e.message : '確認に失敗しました', 'error'); }
  };

  // タブ切り替え: 書類管理 / 取引管理
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'deals' ? 'deals' : 'documents';
  const [activeTab, setActiveTab] = useState<'documents' | 'deals'>(initialTab);
  const autoOpenDealId = searchParams.get('deal') || undefined;

  // 初回ロードのみ全画面ローディング(検索入力時の re-fetch で画面が消えないように)
  if (loading && !contractsData) return <div className={styles.page}>読み込み中...</div>;

  const typeStats = (type: string) => {
    const list = (allContractsForStats ?? []).filter(c => c.contract_type === type);
    return { total: list.length, signed: list.filter(c => c.status === 'signed').length, pending: list.filter(c => c.status === 'pending').length, draft: list.filter(c => c.status === 'draft').length, sent: list.filter(c => c.status === 'sent').length, list };
  };
  const selectedProp = selected?.property_id ? (properties ?? []).find(p => p.id === selected.property_id) : null;
  const typePanelStats = typePanel ? typeStats(typePanel) : null;
  const typePanelContracts = typePanelStats?.list ?? [];
  const statusIdx = ['draft', 'pending', 'sent', 'signed'].indexOf(selected?.status ?? 'draft');

  return (
    <div className={styles.page}>
      <TemplateUpdateBanner />

      {/* タブ切り替え */}
      <div className={styles.tabBar}>
        <button className={`${styles.tab} ${activeTab === 'documents' ? styles.tabActive : ''}`} onClick={() => setActiveTab('documents')}>
          📋 書類管理
        </button>
        <button className={`${styles.tab} ${activeTab === 'deals' ? styles.tabActive : ''}`} onClick={() => setActiveTab('deals')}>
          🏢 取引管理
        </button>
      </div>

      {/* 取引管理タブ */}
      {activeTab === 'deals' && (
        <DealManagement
          properties={properties ?? []}
          customers={customers ?? []}
          autoOpenDealId={autoOpenDealId}
          onAutoOpenConsumed={() => {
            // URLからdeal/tabクエリを除去（戻るボタンでリフローしないように）
            const next = new URLSearchParams(searchParams);
            next.delete('deal');
            next.delete('tab');
            setSearchParams(next, { replace: true });
          }}
          onOpenContract={(contract) => {
            setActiveTab('documents');
            setSelected(contract as unknown as Contract);
          }}
          onOpenExcel={(dealId, propertyId, customerId, propertyName, customerName, category, contractId) => {
            setExcelDealId(dealId);
            setExcelPropertyId(propertyId);
            setExcelCustomerId(customerId);
            setExcelPropertyName(propertyName);
            setExcelCustomerName(customerName);
            setExcelCategory(category);
            setExcelContractId(contractId);
            setExcelExistingDocId(undefined);
            setShowExcelModal(true);
          }}
          onOpenExcelDraft={(docId) => {
            setExcelExistingDocId(docId);
            setShowExcelModal(true);
          }}
        />
      )}

      {/* 書類管理タブ（既存のコンテンツ） */}
      {activeTab === 'documents' && <>
      {/* 書類カードグリッド */}
      <div className={styles.docGrid}>
        {docCards.map((d) => {
          const stats = typeStats(d.type);
          return (
            <div key={d.type} className={styles.docCard} onClick={() => d.type === 'excel_template' ? setShowExcelModal(true) : setTypePanel(d.type)}>
              <div className={styles.docIcon}>{d.icon}</div>
              <div className={styles.docTitle}>{d.title}</div>
              <div className={styles.docDesc}>{d.desc}</div>
              <div className={styles.docStats}>
                <span>今月 {stats.total}件作成</span>
                <span className={`${styles.docBadge} ${d.badgeClass ? styles[d.badgeClass] : ''}`}>{d.badge}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* 契約一覧 */}
      <Card title={`📋 最近の契約・書類(全 ${contractsTotal} 件)`} actions={<button className={styles.btnPrimary} onClick={() => handleOpenCreate()}>＋ 新規作成</button>}>
        {/* 検索(左)+ ページャ(中央)+ 表示件数(右) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, padding: '4px 4px 12px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ justifySelf: 'start' }}>
            <input
              type="search"
              placeholder="🔍 顧客名・書類名で検索"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              style={{ minWidth: 260, maxWidth: 380, padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6 }}
            />
          </div>
          <div style={{ justifySelf: 'center', display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--ink2)' }}>
            <button className={styles.btnOutline} style={{ padding: '6px 14px' }} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || loading}>‹ 前へ</button>
            <span><strong style={{ fontFamily: 'var(--mono)' }}>{page}</strong> / {contractsTotalPages} ページ</span>
            <button className={styles.btnOutline} style={{ padding: '6px 14px' }} onClick={() => setPage(p => Math.min(contractsTotalPages, p + 1))} disabled={page >= contractsTotalPages || loading}>次へ ›</button>
          </div>
          <div style={{ justifySelf: 'end', display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--ink2)' }}>
            <label>表示:</label>
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} style={{ padding: '4px 6px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6 }}>
              <option value={10}>10 件</option>
              <option value={20}>20 件</option>
              <option value={50}>50 件</option>
            </select>
          </div>
        </div>
        <div className={styles.contractList}>
          {(contracts ?? []).map((c) => {
            // 保管期限バッジ: 30 日以内 = red、180 日以内 = amber、期限切れ = red
            let retentionBadge: React.ReactNode = null;
            if (c.retention_until) {
              const d = new Date(c.retention_until);
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const diffDays = Math.ceil((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
              if (diffDays < 0) {
                retentionBadge = <Badge variant="red">🗓 期限切れ</Badge>;
              } else if (diffDays < 30) {
                retentionBadge = <Badge variant="red">🗓 残 {diffDays} 日</Badge>;
              } else if (diffDays < 180) {
                retentionBadge = <Badge variant="amber">🗓 残 {Math.ceil(diffDays / 30)} ヶ月</Badge>;
              }
            }
            return (
              <div key={String(c.id)} className={styles.contractItem} onClick={() => setSelected(c)}>
                <div className={styles.contractIcon}>{typeIcon[c.contract_type] ?? '📄'}</div>
                <div className={styles.contractBody}>
                  <div className={styles.contractTitle}>{c.customer_name}様 ｜ {c.title}</div>
                  <div className={styles.contractMeta}>
                    {statusActionLabel[c.status] ?? c.status}{c.assignee && <> · {c.assignee}</>}
                    {c.retention_until && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ink3)' }}>
                        保管期限 {new Date(c.retention_until).toLocaleDateString('ja-JP')}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.contractRight}>
                  <div className={styles.contractDate}>{new Date(c.created_at).toLocaleDateString('ja-JP')}</div>
                  {c.amount ? <div className={styles.contractAmount}>¥{c.amount.toLocaleString()}</div> : null}
                </div>
                {retentionBadge}
                <Badge variant={statusVariant[c.status] ?? 'gray'}>{statusLabel[c.status] ?? c.status}</Badge>
              </div>
            );
          })}
          {(!contracts || contracts.length === 0) && <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink3)' }}>書類データはありません</div>}
        </div>
      </Card>

      {/* ===== 書類種別 詳細SlidePanel ===== */}
      <SlidePanel open={!!typePanel} onClose={() => setTypePanel(null)} title={typePanel ? `${typeIcon[typePanel]} ${typeLabel[typePanel]}` : ''} width={600}
        actions={typePanel ? <div style={{ display: 'flex', gap: 6 }}>
          {typePanel === 'application' ? <>
            <button className={styles.btnOutline} onClick={() => { if (applyForms.length > 0) { const url = buildApplyUrl(applyForms[0].token, { slug: tenantSlug }); navigator.clipboard.writeText(url); toast('URLをコピーしました', 'success'); } else { toast('先にフォームを作成してください', 'info'); } }}>🔗 リンク共有</button>
            <button className={styles.btnPrimary} onClick={() => { setApplyPropertyId(''); setShowApplyCreate(true); fetchApplyForms(); }}>＋ フォーム作成</button>
          </> : typePanel === 'owner_report' ? <>
            <button className={styles.btnOutline} onClick={() => { ownerReports.filter(r => r.status === 'draft').forEach(r => handleOwnerReportPdf(r)); }}>{'\uD83D\uDCC5 \u4E00\u62ECDL'}</button>
            <button className={styles.btnPrimary} onClick={handleOwnerBulkSend}>{'\uD83D\uDCE7 \u4E00\u62EC\u9001\u4ED8'}</button>
          </> : null}
        </div> : undefined}>
        {typePanel && typePanelStats && <>
          {/* 概要（全種別共通） */}
          <PanelSection title="概要">
            <SpecGrid>
              <SpecCell label={typePanel === 'application' ? '今月受付数' : '今月作成数'} value={`${typePanelStats.total}件`} />
              <SpecCell label={typePanel === 'important_explanation' ? '宅建士確認済' : typePanel === 'management' ? '有効契約数' : typePanel === 'application' ? '審査中' : '署名完了'} value={typePanel === 'management' ? `${typePanelStats.signed + typePanelStats.total}件` : `${typePanelStats.signed}件`} />
              <SpecCell label={typePanel === 'important_explanation' ? '確認待ち' : typePanel === 'management' ? '今月更新' : typePanel === 'application' ? '審査通過' : typePanelStats.sent > 0 ? '署名待ち' : '作成中'} value={`${typePanelStats.pending || typePanelStats.sent || typePanelStats.draft}件`} />
            </SpecGrid>
            {typePanel === 'lease' && <div className={styles.typeNote} style={{ background: 'rgba(59,130,246,0.08)', color: '#3b82f6', borderColor: 'rgba(59,130,246,0.2)' }}>🤖 顧客・物件データから賃料・敷礼金・契約期間を自動入力。電子署名リンクを借主・貸主へ自動送付します。</div>}
            {typePanel === 'important_explanation' && <div className={styles.typeNote} style={{ background: '#fffbeb', color: '#b45c08', borderColor: 'rgba(178,92,8,0.25)' }}>⚖️ 重要事項説明書は宅建士が記名・押印（電子署名）することが法律で義務付けられています。必ず宅建士の確認を経てください。</div>}
            {typePanel === 'application' && <div className={styles.typeNote} style={{ background: 'rgba(139,92,246,0.08)', color: '#7c3aed', borderColor: 'rgba(103,64,200,0.2)' }}>📋 申込書URLを顧客に送付すると、顧客がWebフォームに直接入力できます。送信後、自動で審査会社へ申請できます。</div>}
          </PanelSection>

          {/* ===== オーナー収支報告書: 年月選択→サマリー ===== */}
          {typePanel === 'owner_report' && (
            <PanelSection title={'\u5BFE\u8C61\u5E74\u6708\u3092\u9078\u629E'}>
              <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="month" value={ownerMonth} onChange={e => { setOwnerMonth(e.target.value); fetchOwnerReports(e.target.value); }} className={styles.formInput} style={{ width: 160 }} />
                <button className={styles.btnPrimary} onClick={handleGenerateOwnerReports} disabled={generatingOwner}>{generatingOwner ? '\u751F\u6210\u4E2D...' : '\u751F\u6210\u3059\u308B'}</button>
                <span style={{ fontSize: '12.5px', color: 'var(--ink3)' }}>{ownerReports.length > 0 ? `${ownerReports.length}\u4EF6\u751F\u6210\u6E08` : ''}</span>
              </div>
            </PanelSection>
          )}
          {typePanel === 'owner_report' && (
            <PanelSection title={`${ownerMonth} \u306E\u30B5\u30DE\u30EA\u30FC`}>
              <SpecGrid>
                <SpecCell label={'\u5BFE\u8C61\u30AA\u30FC\u30CA\u30FC\u6570'} value={`${ownerReports.length}\u540D`} />
                <SpecCell label={'\u5BB6\u8CC3\u53CE\u5165\u5408\u8A08'} value={`\u00A5${ownerReports.reduce((s, r) => s + r.rent_income, 0).toLocaleString()}`} mono />
                <SpecCell label={'\u7BA1\u7406\u624B\u6570\u6599\u5408\u8A08'} value={`\u00A5${ownerReports.reduce((s, r) => s + r.management_fee, 0).toLocaleString()}`} mono />
                <SpecCell label={'\u9001\u91D1\u984D\u5408\u8A08'} value={`\u00A5${ownerReports.reduce((s, r) => s + r.transfer_amount, 0).toLocaleString()}`} mono />
              </SpecGrid>
            </PanelSection>
          )}

          {/* ===== 重説: 宅建士確認フロー→確認待ち一覧 ===== */}
          {typePanel === 'important_explanation' && signFlowDefs[typePanel] && (
            <PanelSection title="宅建士確認フロー">
              <div className={styles.flowList}>
                {signFlowDefs[typePanel].steps.map((step, i) => (
                  <div key={i} className={styles.flowStep}><div className={styles.flowNum}>{i + 1}</div><div className={styles.flowInfo}><div className={styles.flowTitle}>{step}</div><div className={styles.flowDesc}>{signFlowDefs[typePanel].descs[i]}</div></div></div>
                ))}
              </div>
            </PanelSection>
          )}
          {typePanel === 'important_explanation' && (
            <PanelSection title={`確認待ちの書類（${typePanelContracts.length}件）`}>
              {typePanelContracts.map(c => (
                <div key={String(c.id)} className={styles.docRow} onClick={() => { setTypePanel(null); setSelected(c); }}>
                  <div className={styles.docRowIcon} style={{ background: c.status === 'signed' ? '#ecfdf5' : '#fffbeb' }}>{c.status === 'signed' ? '✅' : '⏳'}</div>
                  <div className={styles.docRowInfo}>
                    <div className={styles.docRowMain}>{c.customer_name}様 ｜ {c.title}</div>
                    <div className={styles.docRowSub}>{c.status === 'signed' ? '確認済' : '宅建士確認待ち'} · {fmtDate(c.created_at)}{c.assignee && ` · ${c.assignee}`}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Badge variant={c.status === 'signed' ? 'green' : 'amber'}>{c.status === 'signed' ? '確認済' : '確認待ち'}</Badge>
                    {c.status !== 'signed' && <button className={styles.btnPrimary} style={{ padding: '4px 10px', fontSize: 11 }} onClick={(e) => { e.stopPropagation(); setTypePanel(null); setSelected(c); }}>確認する</button>}
                  </div>
                </div>
              ))}
              {typePanelContracts.length === 0 && <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink4)', fontSize: 13 }}>書類なし</div>}
            </PanelSection>
          )}

          {/* ===== 入居申込書: フォーム一覧 ===== */}
          {typePanel === 'application' && (
            <PanelSection title={`\u7533\u8FBC\u30D5\u30A9\u30FC\u30E0\u4E00\u89A7\uFF08${applyForms.length}\u4EF6\uFF09`}>
              {applyForms.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink4)', fontSize: 13 }}>{'\u300C\uFF0B \u30D5\u30A9\u30FC\u30E0\u4F5C\u6210\u300D\u304B\u3089\u4F5C\u6210\u3057\u3066\u304F\u3060\u3055\u3044'}</div>
              ) : applyForms.map(af => {
                const url = buildApplyUrl(af.token, { slug: tenantSlug });
                return (
                  <div key={af.id} className={styles.urlRow} style={{ marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{af.property_name || '\u7269\u4EF6\u672A\u9078\u629E'}</div>
                      <div className={styles.urlBox} style={{ fontSize: 11 }}>{url}</div>
                    </div>
                    <button className={styles.btnOutline} onClick={() => { navigator.clipboard.writeText(url); toast('URL\u3092\u30B3\u30D4\u30FC\u3057\u307E\u3057\u305F', 'success'); }}>📋</button>
                    <button className={styles.btnOutline} onClick={() => { setShareUrl(url); setShowShareModal(true); }}>🔗</button>
                  </div>
                );
              })}
            </PanelSection>
          )}

          {/* ===== 今月の一覧（重説・オーナー収支以外） ===== */}
          {typePanel !== 'important_explanation' && typePanel !== 'owner_report' && <PanelSection title={`今月の${typeLabel[typePanel]}（${typePanelStats.total}件）`}>
            {typePanelContracts.map(c => (
              <div key={String(c.id)} className={styles.docRow} onClick={() => { setTypePanel(null); setSelected(c); }}>
                <div className={styles.docRowIcon} style={{ background: c.status === 'signed' ? '#ecfdf5' : c.status === 'pending' || c.status === 'sent' ? '#fffbeb' : '#eff6ff' }}>
                  {c.status === 'signed' ? '✅' : c.status === 'pending' || c.status === 'sent' ? '⏳' : '📝'}
                </div>
                <div className={styles.docRowInfo}>
                  <div className={styles.docRowMain}>{c.customer_name}様 ｜ {c.title}</div>
                  <div className={styles.docRowSub}>{statusActionLabel[c.status]} · {fmtDate(c.created_at)}{c.assignee && ` · ${c.assignee}`}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  {c.amount ? <div style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--brand)', fontSize: 13 }}>¥{c.amount.toLocaleString()}{c.contract_type !== 'sales' ? '/月' : ''}</div> : null}
                  <Badge variant={statusVariant[c.status] ?? 'gray'}>{statusLabel[c.status] ?? c.status}</Badge>
                </div>
              </div>
            ))}
            {typePanelStats.total === 0 && <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink4)', fontSize: 13 }}>書類なし</div>}
          </PanelSection>}

          {/* ===== 売買契約書: 主要記載項目→AI項目 ===== */}
          {typePanel === 'sales' && (
            <PanelSection title="売買契約書の主要記載項目">
              <SpecGrid>
                <SpecCell label="売買価格" value={typePanelContracts[0]?.amount ? `¥${typePanelContracts[0].amount.toLocaleString()}（税込）` : '—'} mono />
                <SpecCell label="手付金" value={typePanelContracts[0]?.amount ? `¥${Math.round(typePanelContracts[0].amount * 0.1).toLocaleString()}（10%）` : '—'} mono />
                <SpecCell label="残代金支払日" value="—" />
                <SpecCell label="引渡し日" value="—" />
                <SpecCell label="ローン特約" value="—" />
                <SpecCell label="契約不適合責任" value="引渡し後3ヶ月" />
              </SpecGrid>
            </PanelSection>
          )}

          {/* ===== 管理委託: 標準設定 ===== */}
          {typePanel === 'management' && (
            <PanelSection title="管理委託契約の標準設定">
              <SpecGrid>
                <SpecCell label="管理手数料" value="家賃の 5%（税込）" mono />
                <SpecCell label="契約期間" value="2年間（自動更新）" />
                <SpecCell label="解約予告" value="3ヶ月前告知" />
                <SpecCell label="送金日" value="毎月末日" />
              </SpecGrid>
            </PanelSection>
          )}

          {/* ===== AI自動入力項目（入居申込書・オーナー収支・管理委託は不要） ===== */}
          {aiFields[typePanel] && typePanel !== 'application' && typePanel !== 'owner_report' && typePanel !== 'management' && (
            <PanelSection title="AI 自動入力される項目">
              <div className={styles.aiTags}>
                {aiFields[typePanel].auto.map(f => <Badge key={f} variant="blue">{f}</Badge>)}
                {aiFields[typePanel].manual.map(f => <Badge key={f} variant="gray">{f}（手動）</Badge>)}
              </div>
            </PanelSection>
          )}

          {/* ===== 賃貸借: 署名フロー（AI項目の後） ===== */}
          {typePanel === 'lease' && signFlowDefs[typePanel] && (
            <PanelSection title="電子署名フロー">
              <div className={styles.flowList}>
                {signFlowDefs[typePanel].steps.map((step, i) => (
                  <div key={i} className={styles.flowStep}><div className={styles.flowNum}>{i + 1}</div><div className={styles.flowInfo}><div className={styles.flowTitle}>{step}</div><div className={styles.flowDesc}>{signFlowDefs[typePanel].descs[i]}</div></div></div>
                ))}
              </div>
            </PanelSection>
          )}

          {/* ===== 入居申込書: 受付済み申込一覧 ===== */}
          {typePanel === 'application' && (
            <PanelSection title={`\u53D7\u4ED8\u6E08\u307F\u7533\u8FBC\uFF08${submissions.length}\u4EF6\uFF09`}>
              {submissions.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink4)', fontSize: 13 }}>{'\u7533\u8FBC\u306F\u307E\u3060\u3042\u308A\u307E\u305B\u3093'}</div>
              ) : submissions.map(s => (
                <div key={s.id} className={styles.docRow} onClick={() => setSelectedSub(s)} style={{ cursor: 'pointer' }}>
                  <div className={styles.docRowIcon} style={{ background: s.status === 'approved' ? '#ecfdf5' : s.status === 'rejected' ? '#fef2f2' : '#eff6ff' }}>
                    {s.status === 'approved' ? '\u2705' : s.status === 'rejected' ? '\u274C' : '\uD83D\uDCCB'}
                  </div>
                  <div className={styles.docRowInfo}>
                    <div className={styles.docRowMain}>{s.data?.name || '\u540D\u524D\u306A\u3057'}{s.property_name ? ` | ${s.property_name}` : ''}</div>
                    <div className={styles.docRowSub}>{new Date(s.created_at).toLocaleDateString('ja-JP')}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {s.has_document && <Badge variant="blue">書面化済</Badge>}
                    <Badge variant={subStatusVariant[s.status] ?? 'gray'}>{subStatusLabel[s.status] ?? s.status}</Badge>
                  </div>
                </div>
              ))}
            </PanelSection>
          )}

          {/* ===== 入居申込書: 連携審査会社 ===== */}
          {typePanel === 'application' && (
            <PanelSection title="連携審査会社">
              <div className={styles.shinsaGrid}>
                {[['エポス', 'エポス少額短期保険', '1〜2営業日'], ['SUUMO', 'SUUMO住宅サポート', '2〜3営業日'], ['4S', 'フォーシーズンズ', '3〜5営業日'], ['全保連', '全国保証', '2〜4営業日']].map(([logo, name, days]) => (
                  <div key={logo} className={styles.shinsaCard}>
                    <div className={styles.shinsaLogo}>{logo}</div>
                    <div><div style={{ fontWeight: 600, fontSize: 13 }}>{name}</div><div style={{ fontSize: 11, color: 'var(--ink3)' }}>審査: {days}</div></div>
                  </div>
                ))}
              </div>
            </PanelSection>
          )}

          {/* オーナー収支: レポート一覧 */}
          {typePanel === 'owner_report' && (
            <PanelSection title={`\u30AA\u30FC\u30CA\u30FC\u4E00\u89A7\uFF08${ownerReports.length}\u540D\uFF09`}>
              {ownerReports.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink4)', fontSize: 13 }}>{'\u300C\u751F\u6210\u3059\u308B\u300D\u3092\u62BC\u3057\u3066\u30EC\u30DD\u30FC\u30C8\u3092\u4F5C\u6210\u3057\u3066\u304F\u3060\u3055\u3044'}</div>
              ) : ownerReports.map(r => (
                <div key={r.id} className={styles.docRow}>
                  <div className={styles.ownerAvatar}>{r.owner_name.charAt(0)}</div>
                  <div className={styles.docRowInfo}>
                    <div className={styles.docRowMain}>{r.owner_name}</div>
                    <div className={styles.docRowSub}>{'\u7269\u4EF6'} {r.properties.length}{'\u4EF6'} | {'\u9001\u91D1'} {'\u00A5'}{r.transfer_amount.toLocaleString()}</div>
                  </div>
                  <Badge variant={r.status === 'sent' ? 'green' : 'gray'}>{r.status === 'sent' ? '\u9001\u4ED8\u6E08' : '\u672A\u9001\u4ED8'}</Badge>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className={styles.btnOutline} style={{ padding: '3px 8px', fontSize: 11 }} onClick={(e) => { e.stopPropagation(); handleOwnerReportPdf(r); }}>{'\uD83D\uDCC4 PDF'}</button>
                    {r.status !== 'sent' && <button className={styles.btnPrimary} style={{ padding: '3px 8px', fontSize: 11 }} onClick={(e) => { e.stopPropagation(); handleOwnerReportSend(r.id); }}>{'\u9001\u4ED8'}</button>}
                  </div>
                </div>
              ))}
            </PanelSection>
          )}

          {/* オーナー収支: 送付設定 */}
          {typePanel === 'owner_report' && (
            <PanelSection title="送付設定">
              <SpecGrid>
                <SpecCell label="送付方法" value="メール（PDF添付）" />
                <SpecCell label="送付日" value="毎月末日 自動送付" />
                <SpecCell label="件名テンプレ" value="【愛心不動産】{年月}収支報告書" />
                <SpecCell label="押印" value="電子印鑑（自動）" />
              </SpecGrid>
            </PanelSection>
          )}
        </>}
      </SlidePanel>

      {/* ===== 個別契約 詳細SlidePanel ===== */}
      {/* 紙運用: 署名済 PDF アップロード用の隠し input */}
      <input
        ref={signedPdfInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={handleSignedPdfUpload}
        disabled={uploadingSignedPdf}
      />

      <SlidePanel open={!!selected} onClose={() => setSelected(null)} title={selected ? `${selected.title} — ${selected.customer_name}様` : ''} width={580}
        actions={selected ? <div style={{ display: 'flex', gap: 6 }}>
          {selected.contract_type === 'application' ? (
            <>
              {selectedExcelDocId && (
                <button className={styles.btnOutline} onClick={async () => {
                  try {
                    const { token: tk, user: u } = useAuthStore.getState();
                    const hdrs: Record<string, string> = {};
                    if (tk) hdrs['Authorization'] = `Bearer ${tk}`;
                    if (u?.tenantSlug) hdrs['X-Tenant-Slug'] = u.tenantSlug;
                    const res = await fetch(`/api/tenant/excel-documents/${selectedExcelDocId}/download-pdf`, { headers: hdrs });
                    if (!res.ok) { toast('PDFが生成されていません', 'error'); return; }
                    const blob = await res.blob();
                    window.open(URL.createObjectURL(blob), '_blank');
                  } catch { toast('PDF表示に失敗しました', 'error'); }
                }}>📄 PDF</button>
              )}
            </>
          ) : (
            <>
              {selectedExcelDocId && (
                <button className={styles.btnOutline} onClick={async () => {
                  try {
                    const { token, user } = useAuthStore.getState();
                    const headers: Record<string, string> = {};
                    if (token) headers['Authorization'] = `Bearer ${token}`;
                    const slug = user?.tenantSlug; if (slug) headers['X-Tenant-Slug'] = slug;
                    const res = await fetch(`/api/tenant/excel-documents/${selectedExcelDocId}/download-pdf`, { headers });
                    if (!res.ok) { toast('PDFが生成されていません', 'error'); return; }
                    const blob = await res.blob();
                    window.open(URL.createObjectURL(blob), '_blank');
                  } catch { toast('PDF表示に失敗しました', 'error'); }
                }}>📄 PDF</button>
              )}
              <button className={styles.btnOutline} onClick={async () => { try { await api(`/tenant/contracts/${selected.id}/resend`, { method: 'POST' }); toast('署名リンクを送付しました', 'success'); } catch { toast('送信に失敗しました', 'error'); } }}>📧 署名リンク送付</button>
              <button
                className={styles.btnOutline}
                title="紙運用: 手書きサイン・捺印済の契約書 PDF をアップロードして署名済にする"
                onClick={() => signedPdfInputRef.current?.click()}
              >📎 署名済 PDF</button>
            </>
          )}
          <button className={styles.btnPrimary} onClick={handleOpenEdit}>✏️ 編集</button>
        </div> : undefined}>
        {selected && <>
          <PanelSection title="電子署名フロー">
            <div className={styles.signFlow}>
              {(signFlowDefs[selected.contract_type]?.steps ?? ['書類生成', '貸主署名', '借主署名', '宅建士確認', '完了']).map((step, i) => {
                const done = selected.status === 'signed' || i <= statusIdx;
                return (<div key={step} className={`${styles.signStep} ${done ? styles.signDone : ''}`}><div className={styles.signDot}>{done ? '✓' : (i + 1)}</div><div className={styles.signLabel}>{step}</div>{done && <div className={styles.signDate}>{fmtDate(selected.created_at)}</div>}</div>);
              })}
            </div>
            {selected.status === 'signed' && <div className={styles.signSuccess}><span>✅</span><div><div style={{ fontWeight: 600, color: 'var(--green)' }}>電子署名が完了しました</div><div style={{ fontSize: 12, color: 'var(--ink2)', marginTop: 2 }}>{new Date(selected.created_at).toLocaleString('ja-JP')} — 全署名者が完了</div></div></div>}
            {selected.signed_pdf_url && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, fontSize: 13 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>📎 署名済 PDF(紙運用)</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <a href={selected.signed_pdf_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand)', fontSize: 12, textDecoration: 'underline' }}>署名済 PDF を開く</a>
                  {selected.signed_pdf_uploaded_at && (
                    <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
                      アップロード: {new Date(selected.signed_pdf_uploaded_at).toLocaleString('ja-JP')}
                    </span>
                  )}
                </div>
              </div>
            )}
            {selected.retention_until && (() => {
              const d = new Date(selected.retention_until);
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const diffDays = Math.ceil((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
              const warn = diffDays < 30;
              const caution = diffDays >= 30 && diffDays < 180;
              const expired = diffDays < 0;
              const color = expired ? '#dc2626' : warn ? '#dc2626' : caution ? '#f59e0b' : 'var(--ink3)';
              const bg = expired || warn ? '#fef2f2' : caution ? '#fff8e1' : 'var(--surface2)';
              const label = expired ? `期限切れ(${-diffDays}日経過)` : warn ? `あと${diffDays}日` : caution ? `あと約${Math.ceil(diffDays / 30)}ヶ月` : `あと約${Math.ceil(diffDays / 365)}年`;
              return (
                <div style={{ marginTop: 12, padding: '10px 14px', background: bg, borderRadius: 8, fontSize: 13, borderLeft: `3px solid ${color}` }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>📅 書類保管期限</div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>{d.toLocaleDateString('ja-JP')}</span>
                    <span style={{ fontSize: 11, color, fontWeight: warn || expired ? 700 : 400 }}>{label}</span>
                    <span style={{ fontSize: 10, color: 'var(--ink4)' }}>
                      (宅建業法 {selected.contract_type === 'lease' || selected.contract_type === 'sale' ? '10' : '5'} 年保管義務)
                    </span>
                  </div>
                </div>
              );
            })()}
            {selected.contract_type === 'important_explanation' && selected.status !== 'signed' && (
              selected.important_explanation_confirmed_at
                ? <div className={styles.signSuccess}><span>✅</span><div><div style={{ fontWeight: 600, color: 'var(--green)' }}>宅建士確認済み</div><div style={{ fontSize: 12, color: 'var(--ink2)', marginTop: 2 }}>{new Date(selected.important_explanation_confirmed_at).toLocaleString('ja-JP')}</div></div></div>
                : <div className={styles.confirmBox}><div style={{ fontSize: 13, marginBottom: 8 }}><strong>⚠️ 宅建業法第35条</strong>: 宅地建物取引士による重要事項説明の実施と確認が必要です。</div><button className={styles.confirmBtn} onClick={handleConfirmExplanation}>宅建士確認を記録する</button></div>
            )}
          </PanelSection>
          <PanelSection title="契約概要">
            <SpecGrid>
              <SpecCell label="借主" value={selected.customer_name} />
              <SpecCell label="貸主" value={selectedProp?.owner_name || '—'} />
              <SpecCell label="物件" value={selectedProp?.address || '—'} />
              <SpecCell label="間取り" value={selectedProp ? `${selectedProp.layout || '—'} / ${selectedProp.area || '—'}m²` : '—'} />
              <SpecCell label="仲介手数料" value={selected.amount ? `¥${selected.amount.toLocaleString()}` : '—'} mono />
              <SpecCell label="作成日" value={new Date(selected.created_at).toLocaleDateString('ja-JP')} mono />
            </SpecGrid>
          </PanelSection>
          <PanelSection title="AI 自動入力プレビュー">
            <div className={styles.aiPreview}>
              <div className={styles.aiPreviewTitle}>{typeLabel[selected.contract_type] ?? '契約書'}</div>
              {[['契約日', new Date(selected.created_at).toLocaleDateString('ja-JP', { era: 'long' })], ['賃貸人', selectedProp?.owner_name || '—'], ['賃借人', selected.customer_name], ['物件所在地', selectedProp ? `東京都${selectedProp.address}` : '—']].map(([lbl, val]) => (
                <div key={lbl as string} className={styles.aiRow}><span className={styles.aiLabel}>{lbl}</span><span className={styles.aiValue}>{val}<span className={styles.aiAuto}>AUTO</span></span></div>
              ))}
              <div className={styles.aiNote}>🤖 顧客・物件データから AUTO 項目を自動入力しました。内容を確認してください。</div>
            </div>
          </PanelSection>
        </>}
      </SlidePanel>

      {/* ===== 新規作成モーダル（モック準拠） ===== */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title={createFromCard ? `${typeIcon[createType] ?? '📋'} ${typeLabel[createType]}作成` : '📋 書類新規作成'} footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.btnOutline} onClick={() => setShowCreate(false)}>キャンセル</button>
          <button className={styles.btnPrimary} onClick={handleCreate}>書類を生成する</button>
        </div>}>
        <div className={styles.createForm}>
          <div className={styles.formGroup}><label className={styles.formLabel}>書類種別</label>
            {createFromCard ? (
              <div style={{ fontSize: 13, fontWeight: 600, padding: '7px 0' }}>{typeLabel[createType]}</div>
            ) : (
              <select className={styles.formInput} value={createType} onChange={e => setCreateType(e.target.value)}>
                {Object.entries(typeLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            )}
          </div>
          <div className={styles.formRow2}>
            <div className={styles.formGroup}><label className={styles.formLabel}>顧客</label>
              <select className={styles.formInput} value={form.customer_id} onChange={handleCustomerSelect}>
                <option value="">選択してください</option>
                {(customers ?? []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>対象物件</label>
              <select className={styles.formInput} value={form.property_id} onChange={e => {
                const pid = e.target.value; const prop = (properties ?? []).find(p => p.id === pid);
                setForm({ ...form, property_id: pid, amount: prop ? String(Math.round(prop.rent * 1.1)) : form.amount });
              }}>
                <option value="">選択してください</option>
                {(properties ?? []).map(p => <option key={p.id} value={p.id}>{p.name} {p.layout || ''}</option>)}
              </select>
            </div>
          </div>
          {createType !== 'owner_report' && (
            <div className={styles.formRow2}>
              <div className={styles.formGroup}><label className={styles.formLabel}>契約開始日</label>
                <input className={styles.formInput} type="date" value={form.contract_start} onChange={e => setForm({ ...form, contract_start: e.target.value })} />
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>契約終了日</label>
                <input className={styles.formInput} type="date" value={form.contract_end} onChange={e => setForm({ ...form, contract_end: e.target.value })} />
              </div>
            </div>
          )}
          {createType === 'owner_report' && (
            <div className={styles.formGroup}><label className={styles.formLabel}>対象年月</label>
              <input className={styles.formInput} type="month" defaultValue={`${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}`} />
            </div>
          )}
          <div className={styles.formGroup}><label className={styles.formLabel}>{createType === 'owner_report' ? '金額（円）' : createType === 'management' ? '管理手数料（円）' : '仲介手数料（円）'}</label>
            <input className={styles.formInput} type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="198000" />
            {form.property_id && (() => { const prop = (properties ?? []).find(p => p.id === form.property_id); return prop ? <span style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 2, display: 'block' }}>※ 宅建業法上限: 賃料¥{prop.rent?.toLocaleString()} × 1.1 = ¥{Math.round(prop.rent * 1.1).toLocaleString()}</span> : null; })()}
          </div>
          {(createType === 'lease' || createType === 'important_explanation' || createType === 'management') && <>
            <div className={styles.formGroup}><label className={styles.formLabel}>更新種別</label>
              <select className={styles.formInput} value={form.renewal_type} onChange={e => setForm({ ...form, renewal_type: e.target.value })}>
                <option value="agreement">合意更新</option><option value="statutory">法定更新</option><option value="fixed_term">定期借家</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className={styles.formGroup} style={{ flex: 1 }}><label className={styles.formLabel}>契約開始日</label><input className={styles.formInput} type="date" value={form.contract_start} onChange={e => setForm({ ...form, contract_start: e.target.value })} /></div>
              <div className={styles.formGroup} style={{ flex: 1 }}><label className={styles.formLabel}>契約終了日</label><input className={styles.formInput} type="date" value={form.contract_end} onChange={e => setForm({ ...form, contract_end: e.target.value })} /></div>
            </div>
          </>}
          <div className={styles.typeNote} style={{ background: 'rgba(59,130,246,0.08)', color: '#3b82f6', borderColor: 'rgba(59,130,246,0.2)' }}>
            🤖 顧客・物件データから項目を自動入力します。生成後に内容を確認してください。
          </div>
        </div>
      </Modal>

      {/* 編集モーダル */}
      <Modal open={showEdit} onClose={() => { setShowEdit(false); setEditTarget(null); }} title="書類編集" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><button className={styles.btnOutline} onClick={() => { setShowEdit(false); setEditTarget(null); }}>キャンセル</button><button className={styles.btnPrimary} onClick={handleEdit}>保存</button></div>}>
        {editTarget && <div className={styles.createForm}>
          <div className={styles.formGroup}><label className={styles.formLabel}>書類種別</label><div style={{ fontSize: 13, fontWeight: 600, padding: '7px 0' }}>{typeLabel[editTarget.contract_type] ?? editTarget.contract_type}</div></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>書類名</label><input className={styles.formInput} value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} /></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>ステータス</label><select className={styles.formInput} value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>{Object.entries(statusLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>仲介手数料（円）</label><input className={styles.formInput} type="number" value={editForm.amount} onChange={e => setEditForm({ ...editForm, amount: e.target.value })} placeholder="198000" /></div>
          {(editTarget.contract_type === 'lease' || editTarget.contract_type === 'important_explanation' || editTarget.contract_type === 'management') && <>
            <div className={styles.formGroup}><label className={styles.formLabel}>更新種別</label>
              <select className={styles.formInput} value={editForm.renewal_type} onChange={e => setEditForm({ ...editForm, renewal_type: e.target.value })}>
                <option value="agreement">合意更新</option><option value="statutory">法定更新</option><option value="fixed_term">定期借家</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className={styles.formGroup} style={{ flex: 1 }}><label className={styles.formLabel}>契約開始日</label><input className={styles.formInput} type="date" value={editForm.contract_start} onChange={e => setEditForm({ ...editForm, contract_start: e.target.value })} /></div>
              <div className={styles.formGroup} style={{ flex: 1 }}><label className={styles.formLabel}>契約終了日</label><input className={styles.formInput} type="date" value={editForm.contract_end} onChange={e => setEditForm({ ...editForm, contract_end: e.target.value })} /></div>
            </div>
          </>}
        </div>}
      </Modal>
      {/* 申込フォーム作成モーダル */}
      <Modal open={showApplyCreate} onClose={() => setShowApplyCreate(false)} title={'\u2705 \u7533\u8FBC\u30D5\u30A9\u30FC\u30E0\u4F5C\u6210'} footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.btnOutline} onClick={() => setShowApplyCreate(false)}>{'\u30AD\u30E3\u30F3\u30BB\u30EB'}</button>
          <button className={styles.btnPrimary} onClick={handleCreateApplyForm}>{'\u30D5\u30A9\u30FC\u30E0\u3092\u4F5C\u6210'}</button>
        </div>}>
        <div className={styles.createForm}>
          <div className={styles.formGroup}><label className={styles.formLabel}>{'\u5BFE\u8C61\u7269\u4EF6'}</label>
            <select className={styles.formInput} value={applyPropertyId} onChange={e => setApplyPropertyId(e.target.value)}>
              <option value="">{'\u9078\u629E\u3057\u3066\u304F\u3060\u3055\u3044'}</option>
              {(properties ?? []).map(p => <option key={p.id} value={p.id}>{p.name} {p.layout || ''}</option>)}
            </select>
          </div>
          <div className={styles.typeNote} style={{ background: 'rgba(139,92,246,0.08)', color: '#7c3aed', borderColor: 'rgba(103,64,200,0.2)' }}>
            {'\uD83D\uDCCB \u7269\u4EF6\u3092\u9078\u629E\u3059\u308B\u3068\u3001\u9867\u5BA2\u304C\u76F4\u63A5\u5165\u529B\u3067\u304D\u308B\u7533\u8FBC\u30D5\u30A9\u30FC\u30E0URL\u304C\u751F\u6210\u3055\u308C\u307E\u3059\u3002'}
          </div>
        </div>
      </Modal>

      {/* 申込詳細モーダル */}
      <Modal open={!!selectedSub} onClose={() => setSelectedSub(null)} title={'\uD83D\uDCCB \u7533\u8FBC\u8A73\u7D30'} footer={
        selectedSub ? (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button className={styles.btnOutline} onClick={() => setSelectedSub(null)}>閉じる</button>
            {selectedSub.excel_doc_id && (
              <button className={styles.btnOutline} onClick={async () => {
                try {
                  const { token: t, user: u } = useAuthStore.getState();
                  const headers: Record<string, string> = {};
                  if (t) headers['Authorization'] = `Bearer ${t}`;
                  if (u?.tenantSlug) headers['X-Tenant-Slug'] = u.tenantSlug;
                  const res = await fetch(`/api/tenant/excel-documents/${selectedSub.excel_doc_id}/download-pdf`, { headers });
                  if (!res.ok) { toast('PDFが生成されていません', 'error'); return; }
                  const blob = await res.blob();
                  window.open(URL.createObjectURL(blob), '_blank');
                } catch { toast('PDF表示に失敗しました', 'error'); }
              }}>📄 PDF</button>
            )}
            <button className={styles.btnOutline} onClick={() => {
              if (!selectedSub) return;
              setExcelDealId(undefined);
              setExcelPropertyId(undefined);
              setExcelCustomerId(undefined);
              setExcelPropertyName(selectedSub.property_name || undefined);
              setExcelCustomerName((selectedSub.data as any)?.name || undefined);
              setExcelCategory('other');
              setExcelContractId(undefined);
              setExcelSubmissionId(selectedSub.id);
              setShowExcelModal(true);
            }}>📊 書面化</button>
            {selectedSub.status !== 'approved' && <button className={styles.btnPrimary} onClick={() => handleSubStatus(selectedSub.id, 'approved')}>✅ 承認</button>}
            {selectedSub.status !== 'rejected' && <button className={styles.btnOutline} style={{ color: '#dc2626', borderColor: '#fecaca' }} onClick={() => handleSubStatus(selectedSub.id, 'rejected')}>❌ 却下</button>}
            {selectedSub.status === 'received' && <button className={styles.btnOutline} onClick={() => handleSubStatus(selectedSub.id, 'reviewing')}>🔍 審査開始</button>}
          </div>
        ) : undefined
      }>
        {selectedSub && (
          <div className={styles.createForm}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <Badge variant={subStatusVariant[selectedSub.status] ?? 'gray'}>{subStatusLabel[selectedSub.status] ?? selectedSub.status}</Badge>
              <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{new Date(selectedSub.created_at).toLocaleString('ja-JP')}</span>
            </div>
            {selectedSub.property_name && <div className={styles.formGroup}><label className={styles.formLabel}>{'\u7269\u4EF6'}</label><div style={{ fontSize: 13, fontWeight: 600 }}>{selectedSub.property_name}</div></div>}
            {Object.entries(selectedSub.data || {}).filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className={styles.formGroup}>
                <label className={styles.formLabel}>{
                  { name: '\u6C0F\u540D', name_kana: '\u30D5\u30EA\u30AC\u30CA', birth_date: '\u751F\u5E74\u6708\u65E5', phone: '\u96FB\u8A71\u756A\u53F7', email: '\u30E1\u30FC\u30EB', current_address: '\u73FE\u4F4F\u6240', company_name: '\u52E4\u52D9\u5148', company_address: '\u52E4\u52D9\u5148\u4F4F\u6240', company_phone: '\u52E4\u52D9\u5148\u96FB\u8A71', annual_income: '\u5E74\u53CE\uFF08\u4E07\u5186\uFF09', years_employed: '\u52E4\u7D9A\u5E74\u6570', guarantor_name: '\u4FDD\u8A3C\u4EBA\u6C0F\u540D', guarantor_address: '\u4FDD\u8A3C\u4EBA\u4F4F\u6240', guarantor_phone: '\u4FDD\u8A3C\u4EBA\u96FB\u8A71', guarantor_company: '\u4FDD\u8A3C\u4EBA\u52E4\u52D9\u5148', move_in_date: '\u5165\u5C45\u5E0C\u671B\u65E5', num_residents: '\u5165\u5C45\u4EBA\u6570', notes: '\u5099\u8003' }[k] ?? k
                }</label>
                <div style={{ fontSize: 13 }}>{v}</div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* リンク共有モーダル */}
      <Modal open={showShareModal} onClose={() => setShowShareModal(false)} title={'\uD83D\uDD17 \u30EA\u30F3\u30AF\u5171\u6709'} width={600} footer={
        <button className={styles.btnOutline} onClick={() => setShowShareModal(false)}>{'\u9589\u3058\u308B'}</button>
      }>
        <div className={styles.createForm}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{'URL'}</label>
            <div className={styles.urlRow}>
              <div className={styles.urlBox} style={{ flex: 1, fontSize: 12 }}>{shareUrl}</div>
              <button className={styles.btnOutline} style={{ whiteSpace: 'nowrap' }} onClick={() => { navigator.clipboard.writeText(shareUrl); toast('URL\u3092\u30B3\u30D4\u30FC\u3057\u307E\u3057\u305F', 'success'); }}>{'\uD83D\uDCCB \u30B3\u30D4\u30FC'}</button>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{'QR\u30B3\u30FC\u30C9'}</label>
            <div style={{ textAlign: 'center', padding: 12 }}>
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(shareUrl)}`} alt="QR" style={{ borderRadius: 8 }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <a className={styles.btnOutline} href={`mailto:?subject=${encodeURIComponent('\u5165\u5C45\u7533\u8FBC\u30D5\u30A9\u30FC\u30E0')}&body=${encodeURIComponent(shareUrl)}`} style={{ textAlign: 'center', flex: 1, textDecoration: 'none' }}>{'\u2709\uFE0F \u30E1\u30FC\u30EB'}</a>
            <a className={styles.btnOutline} href={`https://line.me/R/msg/text/?${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" style={{ textAlign: 'center', flex: 1, textDecoration: 'none' }}>{'\uD83D\uDCAC LINE'}</a>
          </div>
        </div>
      </Modal>
      </>}
      <PdfGenerateModal
        open={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        templateType={pdfModalType}
        propertyId={pdfPropertyId}
        customerId={pdfCustomerId}
        contractId={pdfContractId}
      />
      <ExcelDocumentModal
        open={showExcelModal}
        onClose={() => { setShowExcelModal(false); setExcelDealId(undefined); setExcelPropertyId(undefined); setExcelCustomerId(undefined); setExcelPropertyName(undefined); setExcelCustomerName(undefined); setExcelCategory(undefined); setExcelContractId(undefined); setExcelSubmissionId(undefined); setExcelExistingDocId(undefined); }}
        properties={properties || []}
        customers={customers || []}
        dealId={excelDealId}
        initialPropertyId={excelPropertyId}
        initialCustomerId={excelCustomerId}
        propertyName={excelPropertyName}
        customerName={excelCustomerName}
        initialCategory={excelCategory}
        contractId={excelContractId}
        submissionId={excelSubmissionId}
        existingDocId={excelExistingDocId}
      />
    </div>
  );
}
